import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UpdateFinancialTransactionBtn } from "./Update";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";
import { Badge } from "@/components/ui/badge";
import { FinancialTransactionProps } from "@/types/financialTransaction";
import {
  deleteFinancialTransactionData,
  getShowFinancialTransaction,
  updateFinancialTransactionStatus,
} from "@/Api/financialTransactions";
import { format } from "date-fns";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { DeleteBtn } from "@/components/Delete";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "@/components/Loaders";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  MoreHorizontal,
  PaperclipIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { AttachmentModal } from "./AttachmentModal";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { MultiSelectActions } from "./MultiSelectActions";
import { Checkbox } from "@/components/ui/checkbox";

interface TableDataProps {
  financialTransactions: FinancialTransactionProps[];
  updateFront: React.Dispatch<
    React.SetStateAction<ApiDataProps<FinancialTransactionProps>>
  >;
  dataApi: ApiDataProps<FinancialTransactionProps>;
  pageActions: TypePageActions;
}

const TRANSACTION_TYPES = {
  ACCOUNTS_PAYABLE: 1,
  ACCOUNTS_RECEIVABLE: 2,
};

export function TableData({
  financialTransactions,
  updateFront,
  dataApi,
  pageActions,
}: TableDataProps) {
  const [openPaymentDateDialog, setOpenPaymentDateDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<FinancialTransactionProps | null>(null);
  const [paymentDate, setPaymentDate] = useState<Date | undefined>(new Date());
  const [loading, setLoading] = useState(false);
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
  const [selectedTransactionUuid, setSelectedTransactionUuid] =
    useState<string>("");
  const { hasPermission } = usePermissions();

  // Hook para seleção múltipla
  const multiSelect = useMultiSelect<FinancialTransactionProps>(
    (item) => item.uuid
  );

  const formatDate = (date: string) => {
    if (!date) return "-";
    try {
      const parsedDate = new Date(date);
      return format(parsedDate, "dd/MM/yyyy HH:mm");
    } catch (error) {
      return date || "-";
    }
  };

  const formatDateOnlyDate = (date: string) => {
    if (!date) return "-";
    try {
      const parsedDate = new Date(date);
      return format(parsedDate, "dd/MM/yyyy");
    } catch (error) {
      return date || "-";
    }
  };

  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case 1:
        return "bg-yellow-500"; // Aberto
      case 2:
        return "bg-green-500"; // Pago
      case 3:
        return "bg-red-500"; // Cancelado
      default:
        return "bg-gray-500";
    }
  };

  const handleStatusChange = async (
    transaction: FinancialTransactionProps,
    newStatusId: number
  ) => {
    if (newStatusId === 2) {
      // Se o status for "Pago", abre o diálogo para seleção da data
      setSelectedTransaction(transaction);
      setPaymentDate(new Date());
      setOpenPaymentDateDialog(true);
    } else {
      await updateTransactionStatus(transaction, newStatusId);
    }
  };

  const updateTransactionStatus = async (
    transaction: FinancialTransactionProps,
    newStatusId: number,
    paymentDate?: Date
  ) => {
    setLoading(true);
    try {
      const payload: any = {
        financial_transaction_status_id: newStatusId,
      };

      if (paymentDate && newStatusId === 2) {
        payload.payment_date = format(paymentDate, "yyyy-MM-dd");
      }

      const { data } = await updateFinancialTransactionStatus(
        transaction.uuid,
        payload
      );

      if (data.status) {
        const updatedData = {
          ...dataApi,
          data: dataApi.data.map((item) =>
            item.uuid === transaction.uuid
              ? {
                  ...data?.response,
                }
              : item
          ),
        };
        updateFront(updatedData);
        toast.success(`Status atualizado com sucesso!`);
      }
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
    } finally {
      setLoading(false);
      setOpenPaymentDateDialog(false);
    }
  };

  const confirmPaymentDate = () => {
    if (selectedTransaction && paymentDate) {
      updateTransactionStatus(selectedTransaction, 2, paymentDate);
    }
  };

  const handleOpenAttachments = (uuid: string) => {
    setSelectedTransactionUuid(uuid);
    setOpenAttachmentModal(true);
  };

  const handleSelectAll = () => {
    if (multiSelect.isAllSelected(financialTransactions)) {
      multiSelect.clearSelection();
    } else {
      multiSelect.selectAll(financialTransactions);
    }
  };

  return (
    <>
      {/* Barra de ações para seleção múltipla */}
      {multiSelect.hasSelection && hasPermission("delete-transaction") && (
        <MultiSelectActions
          selectedItems={multiSelect.selectedItems}
          onClearSelection={multiSelect.clearSelection}
          updateFront={updateFront}
        />
      )}

      <div className="rounded-md border overflow-x-auto">
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c] min-w-[1400px]">
          <TableHeader>
            <TableRow>
              {hasPermission("delete-transaction") && (
                <TableHead className="w-12">
                  <Checkbox
                    checked={multiSelect.isAllSelected(financialTransactions)}
                    onCheckedChange={handleSelectAll}
                    aria-label="Selecionar todas as transações"
                  />
                </TableHead>
              )}
              <TableHead>Tipo</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Forma de Pagamento</TableHead>
              <TableHead className="text-center">Vencimento</TableHead>
              <TableHead className="text-center">Data de Competência</TableHead>
              <TableHead className="text-center">Data de Pagamento</TableHead>
              <TableHead className="text-center">Pagador PIX</TableHead>
              <TableHead className="text-center">Vendedor</TableHead>
              <TableHead className="text-center">Hora do Pagamento</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {financialTransactions?.length > 0 ? (
              financialTransactions?.map((transaction) => {
                const isPayout =
                  transaction.financial_transaction_type_id ===
                  TRANSACTION_TYPES.ACCOUNTS_PAYABLE;

                return (
                  <TableRow key={transaction.id}>
                    {hasPermission("delete-transaction") && (
                      <TableCell>
                        <Checkbox
                          checked={multiSelect.isSelected(transaction)}
                          onCheckedChange={() =>
                            multiSelect.toggleItem(transaction)
                          }
                          aria-label={`Selecionar transação ${transaction.description}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {isPayout ? (
                          <ArrowUp className="h-4 w-4 text-red-500" />
                        ) : (
                          <ArrowDown className="h-4 w-4 text-green-500" />
                        )}
                        <span
                          className={cn(
                            "text-xs",
                            isPayout ? "text-red-600" : "text-green-600"
                          )}
                        >
                          {isPayout ? "Pagar" : "Receber"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell
                      className={cn(
                        "font-medium",
                        isPayout ? "text-red-600" : "text-green-600"
                      )}
                    >
                      {Mask.formatCurrency(transaction.amount)}
                    </TableCell>
                    <TableCell>{transaction.unity?.name || "-"}</TableCell>
                    <TableCell>
                      {isPayout
                        ? transaction.expense_type?.name || "-"
                        : transaction.revenue_type?.name || "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.payment_form?.name || "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.due_date
                        ? formatDateOnlyDate(transaction.due_date)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.competence_date
                        ? formatDateOnlyDate(transaction.competence_date)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.payment_date
                        ? (() => {
                            const date = new Date(transaction.payment_date);
                            if (
                              date.getHours() === 0 &&
                              date.getMinutes() === 0 &&
                              date.getSeconds() === 0
                            ) {
                              return formatDateOnlyDate(
                                transaction?.payment_date
                              );
                            }
                            return formatDate(transaction.payment_date);
                          })()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.sale?.transaction_pix_details?.[0]
                        ?.payer_name || "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.sale?.seller?.user?.name || "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.sale?.transaction_pix_details?.[0]?.hour
                        ? (() => {
                            try {
                              return format(
                                new Date(
                                  transaction.sale.transaction_pix_details[0].hour
                                ),
                                "dd/MM/yyyy HH:mm:ss"
                              );
                            } catch {
                              return transaction.sale.transaction_pix_details[0]
                                .hour;
                            }
                          })()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <If condition={hasPermission("edit-transaction")}>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            className="w-full"
                            disabled={loading}
                          >
                            <div className="flex items-center gap-1 cursor-pointer">
                              <Badge
                                className={cn(
                                  getStatusColor(
                                    transaction.financial_transaction_status_id
                                  )
                                )}
                              >
                                {transaction.financial_transaction_status
                                  ?.name || "-"}
                              </Badge>
                              <ChevronDown className="h-3 w-3" />
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(transaction, 1)}
                              className={
                                transaction.financial_transaction_status_id ===
                                1
                                  ? "bg-yellow-100 dark:bg-yellow-900/30"
                                  : ""
                              }
                            >
                              <Badge className="bg-yellow-500 mr-2">
                                Aberto
                              </Badge>
                              <span>Marcar como aberto</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(transaction, 2)}
                              className={
                                transaction.financial_transaction_status_id ===
                                2
                                  ? "bg-green-100 dark:bg-green-900/30"
                                  : ""
                              }
                            >
                              <Badge className="bg-green-500 mr-2">Pago</Badge>
                              <span>Marcar como pago</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(transaction, 3)}
                              className={
                                transaction.financial_transaction_status_id ===
                                3
                                  ? "bg-red-100 dark:bg-red-900/30"
                                  : ""
                              }
                            >
                              <Badge className="bg-red-500 mr-2">
                                Cancelado
                              </Badge>
                              <span>Marcar como cancelado</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </If>
                      <If condition={!hasPermission("edit-transaction")}>
                        <Badge
                          className={cn(
                            getStatusColor(
                              transaction.financial_transaction_status_id
                            )
                          )}
                        >
                          {transaction.financial_transaction_status?.name ||
                            "-"}
                        </Badge>
                      </If>
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="flex gap-3 w-full justify-center">
                        <If
                          condition={hasPermission(
                            "add-transaction-attachment"
                          )}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                            onClick={() =>
                              handleOpenAttachments(transaction.uuid)
                            }
                          >
                            <PaperclipIcon className="h-4 w-4" />
                          </Button>
                        </If>

                        <If condition={hasPermission("edit-transaction")}>
                          <UpdateFinancialTransactionBtn
                            uuid={transaction.uuid}
                            updateFront={updateFront}
                          />
                        </If>
                        <If condition={hasPermission("delete-transaction")}>
                          <DeleteBtn
                            params={{
                              func: deleteFinancialTransactionData,
                              funcParams: transaction.uuid,
                            }}
                            updateFront={() => {
                              const updatedData = {
                                ...dataApi,
                                data: dataApi.data.filter(
                                  (item) => item.uuid !== transaction.uuid
                                ),
                              };
                              updateFront(updatedData);
                            }}
                            referenceLabel={transaction.description}
                          />
                        </If>
                        <If
                          condition={hasPermission("show-transaction-details")}
                        >
                          <ShowBtn
                            labels={{ title: "Detalhes da Transação" }}
                            params={{
                              func: getShowFinancialTransaction,
                              funcParams: transaction.uuid,
                            }}
                            className="sm:max-w-2xl max-h-[80vh] overflow-y-auto"
                            render={(data: FinancialTransactionProps) => (
                              <div className="flex flex-col gap-3">
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Descrição:
                                  </h3>
                                  <p>{data.description}</p>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Valor:
                                  </h3>
                                  <p>{Mask.formatCurrency(data.amount)}</p>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Unidade:
                                  </h3>
                                  <p>{data.unity?.name || "—"}</p>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    {data.financial_transaction_type_id ===
                                    TRANSACTION_TYPES.ACCOUNTS_PAYABLE
                                      ? "Despesa:"
                                      : "Receita:"}
                                  </h3>
                                  <p>
                                    {data.financial_transaction_type_id ===
                                    TRANSACTION_TYPES.ACCOUNTS_PAYABLE
                                      ? data.expense_type?.name || "—"
                                      : data.revenue_type?.name || "—"}
                                  </p>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Forma de Pagamento:
                                  </h3>
                                  <p>{data.payment_form?.name || "—"}</p>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Vencimento:
                                  </h3>
                                  <p>
                                    {data.due_date
                                      ? formatDate(data.due_date)
                                      : "—"}
                                  </p>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Data de Competência:
                                  </h3>
                                  <p>
                                    {data.competence_date
                                      ? formatDate(data.competence_date)
                                      : "—"}
                                  </p>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Status:
                                  </h3>
                                  <Badge
                                    className={cn(
                                      getStatusColor(
                                        data.financial_transaction_status_id
                                      )
                                    )}
                                  >
                                    {data.financial_transaction_status?.name ||
                                      "-"}
                                  </Badge>
                                </div>
                                <div>
                                  <h3 className="text-sm font-medium">
                                    Observação:
                                  </h3>
                                  <p>{data.observation || "—"}</p>
                                </div>
                                {data.sale?.transaction_pix_details &&
                                  data.sale.transaction_pix_details.length >
                                    0 && (
                                    <div className="border-t pt-4 mt-4">
                                      <h3 className="text-base font-semibold mb-4 text-indigo-700">
                                        🔄 Detalhes das Transações PIX
                                      </h3>
                                      {data.sale.transaction_pix_details.map(
                                        (pixDetail, index) => (
                                          <div
                                            key={pixDetail.id}
                                            className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg border border-indigo-200 dark:border-indigo-700"
                                          >
                                            <div className="flex items-center justify-between mb-3">
                                              <h4 className="text-lg font-semibold text-indigo-800 dark:text-indigo-200">
                                                💰 PIX Transação #{index + 1}
                                              </h4>
                                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                                Processado
                                              </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              {/* Informações do Pagador */}
                                              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
                                                <h5 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                                                  👤 Dados do Pagador
                                                </h5>
                                                <div className="space-y-2 text-sm">
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Nome:
                                                    </span>
                                                    <span className="font-medium text-right max-w-[200px] break-words">
                                                      {pixDetail.payer_name}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Documento:
                                                    </span>
                                                    <span className="font-mono">
                                                      {pixDetail.payer_document}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Tipo Doc:
                                                    </span>
                                                    <span>
                                                      {
                                                        pixDetail.payer_document_type
                                                      }
                                                    </span>
                                                  </div>
                                                  {pixDetail.payer_email && (
                                                    <div className="flex justify-between">
                                                      <span className="text-gray-600 dark:text-gray-400">
                                                        Email:
                                                      </span>
                                                      <span className="text-right max-w-[200px] break-words">
                                                        {pixDetail.payer_email}
                                                      </span>
                                                    </div>
                                                  )}
                                                  {pixDetail.payer_phone && (
                                                    <div className="flex justify-between">
                                                      <span className="text-gray-600 dark:text-gray-400">
                                                        Telefone:
                                                      </span>
                                                      <span>
                                                        {pixDetail.payer_phone}
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Informações da Conta */}
                                              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
                                                <h5 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                                                  🏦 Dados Bancários
                                                </h5>
                                                <div className="space-y-2 text-sm">
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Tipo Conta:
                                                    </span>
                                                    <span className="uppercase">
                                                      {
                                                        pixDetail.payer_account_type
                                                      }
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Agência:
                                                    </span>
                                                    <span className="font-mono">
                                                      {pixDetail.payer_agency}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Conta:
                                                    </span>
                                                    <span className="font-mono">
                                                      {pixDetail.payer_account}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      ISPB:
                                                    </span>
                                                    <span className="font-mono text-xs">
                                                      {pixDetail.payer_ispb}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Informações da Transação */}
                                            <div className="mt-4 bg-white dark:bg-gray-800 p-4 rounded-lg border">
                                              <h5 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                                                📊 Informações da Transação
                                              </h5>
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                <div>
                                                  <div className="flex justify-between mb-2">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Valor:
                                                    </span>
                                                    <span className="font-bold text-green-600 text-lg">
                                                      {Mask.formatCurrency(
                                                        parseFloat(
                                                          pixDetail.amount
                                                        )
                                                      )}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between mb-2">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Data/Hora:
                                                    </span>
                                                    <span className="font-mono text-xs">
                                                      {(() => {
                                                        try {
                                                          return format(
                                                            new Date(
                                                              pixDetail.hour
                                                            ),
                                                            "dd/MM/yyyy HH:mm:ss"
                                                          );
                                                        } catch {
                                                          return pixDetail.hour;
                                                        }
                                                      })()}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between mb-2">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      Chave PIX:
                                                    </span>
                                                    <span className="font-mono text-xs max-w-[150px] break-all">
                                                      {pixDetail.key}
                                                    </span>
                                                  </div>
                                                </div>
                                                <div>
                                                  <div className="flex justify-between mb-2">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      TX ID:
                                                    </span>
                                                    <span className="font-mono text-xs max-w-[150px] break-all">
                                                      {pixDetail.tx_id}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between mb-2">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      End to End ID:
                                                    </span>
                                                    <span className="font-mono text-xs max-w-[150px] break-all">
                                                      {pixDetail.end_to_end_id}
                                                    </span>
                                                  </div>
                                                  <div className="flex justify-between mb-2">
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                      ID Venda:
                                                    </span>
                                                    <span className="font-bold">
                                                      #{pixDetail.sale_id}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Resumo da Transação */}
                                            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                              <div className="flex items-center justify-between text-sm">
                                                <span className="text-blue-700 dark:text-blue-300 font-medium">
                                                  📋 Resumo: Pagamento PIX
                                                  processado com sucesso
                                                </span>
                                                <span className="text-xs text-blue-600 dark:text-blue-400">
                                                  ID: {pixDetail.id}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                {(() => {
                                  const sale = (data.sale ?? {}) as any;
                                  if (!data.sale) return null;
                                  return (
                                    <div className="border-t pt-2 mt-2">
                                      <h3 className="text-base font-semibold mb-2">
                                        Detalhes da Venda
                                      </h3>
                                      <div className="flex flex-col gap-2 text-sm">
                                        <div>
                                          <b>ID:</b> {sale.id}
                                        </div>
                                        <div>
                                          <b>Total:</b>{" "}
                                          {Mask.formatCurrency(sale.total)}
                                        </div>
                                        {sale.created_at && (
                                          <div>
                                            <b>Data:</b>{" "}
                                            {format(
                                              new Date(sale.created_at),
                                              "dd/MM/yyyy HH:mm:ss"
                                            )}
                                          </div>
                                        )}
                                        {sale.sale_type?.name && (
                                          <div>
                                            <b>Tipo:</b> {sale.sale_type.name}
                                          </div>
                                        )}
                                        {sale.unity?.name && (
                                          <div>
                                            <b>Unidade:</b> {sale.unity.name}
                                          </div>
                                        )}
                                        {sale.client?.name && (
                                          <div>
                                            <b>Cliente:</b> {sale.client.name}
                                          </div>
                                        )}
                                        {sale.seller?.user?.name && (
                                          <div>
                                            <b>Vendedor:</b>{" "}
                                            {sale.seller.user.name}
                                          </div>
                                        )}
                                        {sale.seller?.user?.email && (
                                          <div>
                                            <b>Email do Vendedor:</b>{" "}
                                            {sale.seller.user.email}
                                          </div>
                                        )}
                                        <div>
                                          <b>Formas de Pagamento:</b>
                                          <div className="mt-2 space-y-3">
                                            {sale.payment_forms?.map(
                                              (pf: any) => (
                                                <div key={pf.id} className="p-3 bg-muted/50 rounded-lg border text-sm space-y-1">
                                                  <div className="font-medium">
                                                    {pf.payment_form?.name || "—"} - {Mask.formatCurrency(pf.value)}
                                                  </div>
                                                  {pf.sale_code_transaction && pf.sale_code_transaction !== "0" && (
                                                    <div><b>Código:</b> {pf.sale_code_transaction}</div>
                                                  )}
                                                  {pf.payer_name && (
                                                    <div><b>Pagador:</b> {pf.payer_name}</div>
                                                  )}
                                                  {pf.date_hour && (
                                                    <div>
                                                      <b>Data/Hora:</b>{" "}
                                                      {(() => {
                                                        try {
                                                          return format(new Date(pf.date_hour), "dd/MM/yyyy HH:mm");
                                                        } catch {
                                                          return pf.date_hour;
                                                        }
                                                      })()}
                                                    </div>
                                                  )}
                                                  {pf.observation && (
                                                    <div><b>Obs:</b> {pf.observation}</div>
                                                  )}
                                                </div>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            loader={<CreateLoaderInput lines={8} />}
                          />
                        </If>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={hasPermission("delete-transaction") ? 15 : 14}
                  className="h-24 text-center"
                >
                  Nenhuma transação encontrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page <= 1 &&
                      "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    "size-8 bg-transparent border-2 border-blue-600 text-slate-900 dark:text-white font-bold cursor-auto hover:bg-transparent"
                  )}
                >
                  <span className="pointer-events-none">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black dark:text-white h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page >= dataApi?.last_page &&
                      "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      {/* Modal para definir a data de pagamento */}
      <Dialog
        open={openPaymentDateDialog}
        onOpenChange={setOpenPaymentDateDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Data de Pagamento</DialogTitle>
            <DialogDescription>
              Selecione a data em que o pagamento foi realizado.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <Calendar
              mode="single"
              selected={paymentDate}
              onSelect={setPaymentDate}
              className="rounded-md border"
            />
          </div>
          <DialogFooter className="flex flex-row gap-2 justify-between sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setOpenPaymentDateDialog(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              onClick={confirmPaymentDate}
              disabled={!paymentDate || loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? "Processando..." : "Confirmar Pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentModal
        isOpen={openAttachmentModal}
        onClose={() => setOpenAttachmentModal(false)}
        transactionUuid={selectedTransactionUuid}
      />
    </>
  );
}
